import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, MultiSelect, Text, TextInput, ActionIcon } from "@mantine/core";
import { IconTrash, IconPlus } from "@tabler/icons-react";

import Item from "../Item";
import { opTags } from "@dummy/op-tags";
import { occupationalProfile } from "@dummy/occupational-profile";

export default function ProfileSearch({ onSubmit, profiles }) {
  const [tagIds, setTagIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchProfiles();
    fetchTags();
  }, []);

  useEffect(() => {
    setSelectedProfiles(profiles);
  }, [profiles]);

  function fetchProfiles() {
    axios
      .post(`/retrieve-occupational-profiles/`, {
        scope: "active",
        original: "true",
        page: 0,
        page_size: 500,
      })
      .then(({ data }) => {
        const rows = data.response?.[0]?.data || [];
        setAllProfiles(
          [...rows].sort((a, b) =>
            (a.original_name || "") > (b.original_name || "") ? 1 : -1
          )
        );
      })
      .catch(() => {
        setAllProfiles([occupationalProfile]);
      });
  }

  function fetchTags() {
    axios
      .get(`/op-tags/`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.response || [];
        setTags(
          list
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((t) => ({
              value: String(t.id),
              label: t.name,
            }))
        );
      })
      .catch(() => {
        setTags(
          opTags.map((t) => ({
            value: String(t.id),
            label: t.name,
          }))
        );
      });
  }

  function onProfileActionClick(profile) {
    const { id } = profile;

    if (selectedProfiles.find((p) => p.id === id)) {
      return setSelectedProfiles(selectedProfiles.filter((f) => f.id !== id));
    }

    if (selectedProfiles.length === 10) {
      return alert(`You've added the maximum amount of profiles!`);
    }

    setSelectedProfiles([...selectedProfiles, profile]);
  }

  const searchNeedle = searchValue.trim().toLowerCase();
  const filteredProfiles = allProfiles.filter((profile) => {
    const name = (profile.original_name || profile.name || "").toLowerCase();
    if (searchNeedle && !name.includes(searchNeedle)) return false;
    if (!tagIds.length) return true;
    const profileTags = profile.tags || [];
    return profileTags.some((tag) => tagIds.includes(String(tag.id)));
  });

  const selectedProfileIds = selectedProfiles.map((p) => p.id);

  return (
    <div>
      <Text fw={700}>Search Profiles</Text>
      <TextInput
        placeholder="Search by name..."
        value={searchValue}
        onChange={(event) => setSearchValue(event.currentTarget.value)}
        mb="sm"
      />
      <MultiSelect
        placeholder="Select some tags..."
        searchable
        data={tags}
        value={tagIds}
        onChange={setTagIds}
        mb="sm"
      />
      <div className="profile-items">
        {filteredProfiles.map((profile) => (
          <Item
            key={profile.id}
            name={profile.original_name}
            profile={profile}
            tags={profile.tags}
          >
            <ActionIcon
              variant="light"
              size="lg"
              onClick={() => onProfileActionClick(profile)}
              color={selectedProfileIds.includes(profile.id) ? "red" : "green"}
            >
              {selectedProfileIds.includes(profile.id) ? (
                <IconTrash size={16} />
              ) : (
                <IconPlus size={16} />
              )}
            </ActionIcon>
          </Item>
        ))}
      </div>
      {selectedProfiles.length > 0 && (
        <div className="profile-counter">
          <Button
            onClick={() => onSubmit(selectedProfiles)}
            // disabled={selectedProfileIds.length !== 10}
            disabled={selectedProfileIds.length === 0}
            size="xl"
            color="green"
          >
            {selectedProfileIds.length === 10
              ? "Next"
              : `${selectedProfileIds.length}/10 Profiles Selected`}
          </Button>
        </div>
      )}
    </div>
  );
}
